import type {
  IntegrationsPayload,
  TradingAccountIntegration,
} from "@shared/types/integrations";
import { getApiBase } from "./apiConfig";
import { apiClient } from "./apiClient";

const API_BASE = getApiBase();

const EMPTY_PAYLOAD: IntegrationsPayload = {
  summary: {
    totalAccounts: 0,
    totalActiveAccounts: 0,
    hasPendingActions: false,
    lastRefreshedAt: null,
    message: "No integrations configured yet. Connect an exchange account to get started.",
  },
  tradingAccounts: {
    items: [],
    meta: {
      hasItems: false,
      emptyStateMessage:
        "No trading accounts connected yet. Add one to begin trading or monitoring on supported exchanges.",
      lastUpdatedAt: null,
    },
  },
  pendingRequests: {
    items: [],
    meta: {
      hasItems: false,
      emptyStateMessage:
        "No pending requests in the queue. Submit account connection details to begin the verification workflow.",
      lastUpdatedAt: null,
    },
  },
  warnings: [
    "Integrations are not configured yet. Register at least one trading account to enable automation.",
  ],
};

async function parseJsonResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let message = `Request failed with status ${response.status}`;
    try {
      const payload = await response.json();
      if (typeof payload?.detail === "string") {
        message = payload.detail;
      } else if (typeof payload?.message === "string") {
        message = payload.message;
      }
    } catch {
      // Ignore JSON parsing errors and fall back to default message.
    }
    throw new Error(message);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export async function fetchIntegrationsData(): Promise<IntegrationsPayload> {
  try {
    // Use apiClient instead of fetch to handle CORS and CSP properly
    const { apiClient } = await import("./apiClient");
    const response = await apiClient.get<IntegrationsPayload>("/dashboard/integrations/");
    return response.data;
  } catch (error) {
    console.warn("[integrationsApi] Falling back to empty payload:", error);
    return EMPTY_PAYLOAD;
  }
}

export interface RegisterTradingAccountInput {
  provider: string;
  apiKey: string;
  apiSecret: string;
  nickname: string;
  owner?: string | null;
  scopes?: string[];
  metadata?: Record<string, unknown>;
}

export async function registerTradingAccount(
  input: RegisterTradingAccountInput,
): Promise<TradingAccountIntegration> {
  // Use apiClient to ensure CSRF token is included
  const response = await apiClient.post<TradingAccountIntegration>(
    "/dashboard/integrations/accounts",
    input,
  );
  return response.data;
}

export async function activateTradingAccount(accountId: string): Promise<TradingAccountIntegration> {
  // Use apiClient to ensure CSRF token is included
  const response = await apiClient.post<TradingAccountIntegration>(
    `/dashboard/integrations/accounts/${encodeURIComponent(accountId)}/activate`,
  );
  return response.data;
}

export async function refreshAccountSnapshot(accountId: string): Promise<TradingAccountIntegration> {
  // Use apiClient to ensure CSRF token is included
  const response = await apiClient.post<TradingAccountIntegration>(
    `/dashboard/integrations/accounts/${encodeURIComponent(accountId)}/refresh`,
  );
  return response.data;
}

export interface RotateTradingAccountInput {
  apiKey: string;
  apiSecret: string;
}

export async function rotateTradingAccount(
  accountId: string,
  input: RotateTradingAccountInput,
): Promise<TradingAccountIntegration> {
  // Use apiClient to ensure CSRF token is included
  const response = await apiClient.post<TradingAccountIntegration>(
    `/dashboard/integrations/accounts/${encodeURIComponent(accountId)}/rotate`,
    input,
  );
  return response.data;
}

export async function deleteTradingAccount(accountId: string): Promise<void> {
  // Use apiClient to ensure CSRF token is included
  await apiClient.delete(`/dashboard/integrations/accounts/${encodeURIComponent(accountId)}`);
}

export interface ValidateBybitKeysInput {
  api_key: string;
  api_secret: string;
  testnet?: boolean;
}

export interface ValidateBybitKeysResponse {
  valid: boolean;
  trading_enabled: boolean;
  withdraw_enabled: boolean;
  deposit_enabled: boolean;
  read_only: boolean;
  ip_restricted: boolean | null;
  missing_unified_permissions: string[];
  missing_exchange_permissions: string[];
  permissions?: Record<string, unknown>;
  error?: string;
}

export async function validateBybitKeys(
  input: ValidateBybitKeysInput,
): Promise<ValidateBybitKeysResponse> {
  const response = await apiClient.post<ValidateBybitKeysResponse>(
    "/dashboard/integrations/validate/bybit",
    input,
  );
  return response.data;
}

export interface TelegramStatus {
  is_linked: boolean;
  chat_id: string | null;
  bot_link: string | null;
}

export async function getTelegramStatus(): Promise<TelegramStatus> {
  const response = await apiClient.get<TelegramStatus>("/dashboard/integrations/telegram-status");
  return response.data;
}

export async function unlinkTelegram(): Promise<void> {
  await apiClient.delete("/dashboard/integrations/telegram-link");
}

